// src/contexts/LanguageContext.jsx - Optimized
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { 
  getCurrentLanguage, 
  setLanguage, 
  triggerLanguageChange, 
  SUPPORTED_LANGUAGES,
  t
} from '../i18n';
import { getFromStorage, saveToStorage } from '../utils/storage';
import { STORAGE_KEYS } from '../utils/constants';

// Dil Context'ini oluştur
const LanguageContext = createContext();

/**
 * Dil Sağlayıcısı
 * Uygulamada dil değişimini ve durumunu yönetir
 * 
 * @param {Object} props - React component props
 * @returns {React.Component} Context Provider component
 */
export const LanguageProvider = ({ children }) => {
  // Başlangıç dil bilgisini local storage'dan al
  const [currentLanguage, setCurrentLanguage] = useState(() => 
    getCurrentLanguage());
  
  // Dil yükleme durumu
  const [isChangingLanguage, setIsChangingLanguage] = useState(false);
  
  // Dil değişimi için callback - performans optimizasyonu
  const changeLanguage = useCallback(async (langCode) => {
    // Aynı dili seçiyorsa işlem yapma
    if (langCode === currentLanguage) return true;
    
  console.log(`Dil değiştiriliyor: ${currentLanguage} -> ${langCode}`);
  setIsChangingLanguage(true);
  
  try {
    // i18n modülü aracılığıyla dil değişimini tetikle
    const success = await triggerLanguageChange(langCode);
    
    if (success) {
      // State'i güncelle
      setCurrentLanguage(langCode);
      // State değişimi bittikten sonra
      setTimeout(() => setIsChangingLanguage(false), 300);
      return true;
    } else {
      console.error('Dil değişimi başarısız oldu');
      setIsChangingLanguage(false);
      return false;
    }
  } catch (error) {
    console.error('Dil değişimi hatası:', error);
    setIsChangingLanguage(false);
    return false;
  }
}, [currentLanguage]);
  
  // Bir sonraki dile geç (kolayca dil değiştirmek için)
  const toggleNextLanguage = useCallback(() => {
    const currentIndex = SUPPORTED_LANGUAGES.findIndex(lang => lang.code === currentLanguage);
    const nextIndex = (currentIndex + 1) % SUPPORTED_LANGUAGES.length;
    changeLanguage(SUPPORTED_LANGUAGES[nextIndex].code);
  }, [currentLanguage, changeLanguage]);
  
  // Geçerli dil nesnesini getir
  const getCurrentLanguageObj = useCallback(() => {
    return SUPPORTED_LANGUAGES.find(lang => lang.code === currentLanguage) || SUPPORTED_LANGUAGES[0];
  }, [currentLanguage]);
  
  // Dil ve sayfa yükleme olayını dinle
  useEffect(() => {
    // URL'den dil parametresini al (sayfa yenilemesinde)
    const checkURLLanguage = () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const langParam = urlParams.get('lang');
        
        // Eğer URL'de dil parametresi varsa ve geçerli bir dil ise
        if (langParam && SUPPORTED_LANGUAGES.some(l => l.code === langParam)) {
          console.log(`URL'den dil parametresi algılandı: ${langParam}`);
          
          // URL'den dil parametresini temizle
          if (window.history && window.history.replaceState) {
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.delete('lang');
            window.history.replaceState({}, document.title, newUrl.toString());
          }
          
          // Eğer mevcut dilden farklı ise dil değiştir
          if (langParam !== currentLanguage) {
            setCurrentLanguage(langParam);
          }
        }
        
        // Dil değişim durumunu temizle
        sessionStorage.removeItem('language_changing');
        sessionStorage.removeItem('pending_language');
      } catch (error) {
        console.error('URL dil parametresi kontrolü sırasında hata:', error);
      }
    };
    
    // Sayfa yüklendiğinde ve sonrasında URL dil parametresini kontrol et
    checkURLLanguage();
    
    // Küresel dil değişikliği dinleyicisi
    const handleLanguageChange = (event) => {
      const newLang = event.detail?.language;
      if (newLang && newLang !== currentLanguage) {
        setCurrentLanguage(newLang);
      }
    };
    
    // Dil değişim olayını dinle
    window.addEventListener('app:language:changed', handleLanguageChange);
    
    return () => {
      window.removeEventListener('app:language:changed', handleLanguageChange);
    };
  }, [currentLanguage]);
  
  // Context değerlerini bir araya getir
  const contextValue = {
    currentLanguage,
    languages: SUPPORTED_LANGUAGES,
    changeLanguage,
    toggleNextLanguage,
    isChangingLanguage,
    getCurrentLanguageObj,
    t
  };
  
  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

/**
 * Dil Bağlamı Hook
 * Bir bileşen içinde dil bağlamına erişim sağlar
 * 
 * @returns {Object} Dil bağlamı değerleri ve işlevleri
 */
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  
  if (!context) {
    throw new Error('useLanguage hook must be used within a LanguageProvider');
  }
  
  return context;
};

export default LanguageContext;